import { memo } from 'react';
import { AllContentNoSearchResults } from 'src/components/AllContent/AllContentNoSearchResults';
import { useAllContentActions } from 'src/components/AllContent/hooks/useAllContentActions';

import { useTrackClickedAddContent } from '@air/analytics';
import { Plus } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { DropdownMenu, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { useBreakpointsContext } from '@air/provider-media-query';

import { ContributableNullState } from '~/components/ContributableNullState/ContributableNullState';
import { NonContributableEmptyState } from '~/components/NonContributableNullState/NonContributableNullState';
import { useIsSearchActive } from '~/hooks/search/useIsSearchActive';
import { useUploadFilesFromComputer } from '~/hooks/useUploadFilesFromComputer';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

export type AllContentNullStateProps = {
  title: string;
  description: string;
};

export const NoWorkspaceContentState = memo(({ title, description }: AllContentNullStateProps) => {
  const trackLocation = 'all-content-null-state';
  const { uploadFilesFromComputer } = useUploadFilesFromComputer({ trackLocation });
  const { data: permissions } = useCurrentWorkspacePermissionsContext();
  const canCreateRootBoards = canCreateBoard(permissions);
  const { allContentActionsOptions } = useAllContentActions({ trackLocation });
  const { isSearchActive } = useIsSearchActive();
  const { isAboveMediumScreen } = useBreakpointsContext();
  const { trackClickedAddContent } = useTrackClickedAddContent();

  if (isSearchActive) return <AllContentNoSearchResults />;

  if (isAboveMediumScreen && !!allContentActionsOptions.length) {
    return (
      <ContributableNullState
        title={title}
        description={description}
        cta={
          <DropdownMenu
            size="small"
            trigger={
              <Button
                prefix={<Plus className="size-4" />}
                data-testid="CONTRIBUTABLE_NULL_STATE_CTA_BUTTON"
                appearance="filled"
                color="blue"
                size="large"
              >
                Create new board
              </Button>
            }
            onOpenChange={(isExpanded: boolean) => {
              if (isExpanded) trackClickedAddContent({ location: trackLocation });
            }}
          >
            {renderDropdownItems({ options: allContentActionsOptions })}
          </DropdownMenu>
        }
      />
    );
  }

  if (!isAboveMediumScreen && canCreateRootBoards) {
    return (
      <ContributableNullState
        title={title}
        description={description}
        cta={
          <Button
            prefix={<Plus className="size-4" />}
            appearance="filled"
            color="blue"
            size="large"
            data-testid="CONTRIBUTABLE_NULL_STATE_CTA_BUTTON"
            onClick={() => {
              uploadFilesFromComputer();
              trackClickedAddContent({ location: trackLocation });
            }}
          >
            Upload from your device
          </Button>
        }
      />
    );
  }

  return <NonContributableEmptyState />;
});

NoWorkspaceContentState.displayName = 'NoWorkspaceContentState';
